import { useEffect, useMemo, useState } from "react";
import { IconUserCircle } from "@tabler/icons-react";
import { Trans } from "react-i18next";
import { Link, useHistory } from "react-router-dom";
import { z } from "zod";
import { useChatScrollAnchor } from "../../domains/gpt/hooks/useChatScrollAnchor";
import {
  useConsumeLocationState,
  useLocationState,
} from "../../domains/app/hooks/useLocationState";
import { displayToast } from "../../components/app/AppToast";
import Navbar from "../../components/app/navbars/Navbar";
import { ChatInput } from "../../components/ui/ChatInput";
import { useDictationControl } from "../../domains/gpt/hooks/useDictationControl";
import { useChatComposerAdd } from "../../domains/gpt/hooks/useChatComposerAdd";
import {
  useDesktopLandingMode,
  useGptMode,
  type GptMode,
} from "../../domains/gpt/stores/modeStore";
import { useNewChatGeneration } from "../../domains/gpt/stores/newChatGenerationStore";
import { useKeyboardManaged } from "../../hooks/use-keyboard-managed";
import { ScrollDownButton } from "../../components/ui/ScrollDownButton";
import {
  GPT_DESKTOP_COLUMN,
  GPT_DESKTOP_COMPOSER,
  GptDesktopNotice,
  GPT_HEADER_ROW,
  GPTConversationWrapper,
} from "../../components/gpt/layout";
import { GPTGreeting } from "../../components/gpt/GPTGreeting";
import { DesktopAcquisitionCard } from "./desktop/DesktopAcquisitionCard";
import { GPTToolStep } from "../../components/gpt/chat/GPTToolStep";
import { ChatMessageCard } from "../../components/ui/ChatMessageCard";
import { DesktopTopNav } from "../../components/app/desktopTopNav/DesktopTopNav";
import { GptConversationBreadcrumb } from "../../components/gpt/GptConversationBreadcrumb";
import { askedFromSchema, type AskedFrom } from "../../domains/gpt/askedFrom";
import {
  GPTHistoryButton,
  GPTHistoryPanel,
  GPTHistoryScrim,
  gptHistoryArrivalSchema,
  useGptHistoryReveal,
} from "../../components/gpt/GPTHistoryPanel";
import { isDesktopShell } from "../../common/hooks/useIsDesktopView";
import { cn } from "../../lib/utils";
import KeyboardAvoidingView from "../../components/layout/KeyboardAvoidingView";
import Loader from "../../components/Loader";
import ProfilePicture from "../../components/ProfilePicture";
import RoomLogo from "../../components/room/RoomLogo";
import { useCurrentUser } from "../../domains/user/hooks/useCurrentUser";
import {
  apiClient,
  seedApiQueryData,
  useApiQuery,
  useApiRevalidate,
} from "../../services/api";
import type { AssistantOrigin } from "../../domains/assistant/assistantChatTransport";
import { useIsRoomWelcomeOpen } from "../../components/room/RoomWelcomeHost";
import { useIsAnnouncementShowing } from "../../features/announcements/AnnouncementHost";
import { WizardInvitation } from "../../features/juiscigpt-wizard/WizardInvitation";
import { useWizardSession } from "../../features/juiscigpt-wizard/wizardSession";
import { useElapsed } from "../../hooks/useElapsed";
import { useTrackOnMount } from "../../hooks/use-track-on-mount";
import { useHandedQuestionOwnership } from "../../domains/gpt/hooks/useHandedQuestionOwnership";
import { usePageLinks } from "../../domains/app/hooks/usePageLinks";
import { DebugLogger } from "../../domains/app/utils/debugLogger";
import { getRoomLogoUrl } from "../../domains/room/utils/getRoomLogoUrl";
import {
  questionOriginSchema,
  type QuestionOrigin,
} from "../../domains/gpt/hooks/useGptChat";
import { useSingleFlightAction } from "../../hooks/useSingleFlightAction";
import { useChatAttachments } from "../../domains/gpt/hooks/useChatAttachments";
import {
  referenceKey,
  useChatReferences,
} from "../../domains/gpt/hooks/useChatReferences";
import { useEnsureGptConversation } from "../../domains/gpt/hooks/useEnsureGptConversation";
import { useConversationEngine } from "../../domains/assistant/useConversationEngine";
import type { Redaction } from "../../domains/assistant/redaction";
import { useQuestionRedaction } from "../../domains/assistant/useQuestionRedaction";
import { useChatComposer } from "../../domains/gpt/hooks/useChatComposer";
import { PatientDataBanner } from "../../components/gpt/chat/PatientDataBanner";
import { RedactedQuestion } from "../../components/gpt/chat/RedactedQuestion";
import { ChatAttachmentChips } from "../../components/gpt/chat/ChatAttachments";
import {
  ChatComposerAddPanel,
  ChatComposerAddTrigger,
} from "../../components/gpt/chat/ChatComposerAdd";
import {
  ChatReferenceChips,
  ChatMentionDropdown,
} from "../../components/gpt/chat/ChatReferences";
import { useChatMention } from "../../domains/gpt/hooks/useChatMention";
import {
  GPTPrePrompts,
  type PrePromptCategoryId,
} from "../../components/gpt/chat/GPTPrePrompts";
import { gaEventGptSessionStarted } from "../../tools/analytics/domains/gpt.analytics";
import ScrollableLayout from "../../components/layout/ScrollableLayout";
import { useTranslation } from "react-i18next";
import { HapticEffect } from "../../interfaces";
import { iosGenerateHaptic } from "../../tools/ios";

const debug = new DebugLogger("JuisciGPT New Chat Page");
debug.mute();

const FEATURE_FLAG_JUISCIGPT_FOR_ROOMS = false;

const locationStateSchema = z.object({
  initialQuestion: z.string().nullable().default(null),
  // How the handed-over question was composed (a Chat-with-paper template, or
  // typed), for the analytics of its send.
  initialQuestionOrigin: questionOriginSchema.default("typed"),
  initialQuestionOwnerUid: z.string().nullable().default(null),
  // The paper an "Ask about this study" hand-off is about (useAskAboutPaper):
  // referenced before the question is asked, so the run reads it.
  initialArticle: z
    .object({ id: z.string().min(1), title: z.string() })
    .nullable()
    .default(null),
  askedFrom: askedFromSchema,
  // The Discovery feed the reader arrived from (`FeedEndAskGpt`), carried into
  // the conversation's create so the agent searches PubMed inside that feed.
  // Router state is a boundary, hence the Zod mirror of the wire union.
  feedScope: z
    .discriminatedUnion("kind", [
      z.object({
        kind: z.literal("practice"),
        uid: z.string().min(1),
        descriptorUi: z.string().min(1).nullable(),
      }),
      z.object({ kind: z.literal("topic"), uid: z.string().min(1) }),
    ])
    .nullable()
    .default(null),
  // How the drawer enters (see `gptHistoryArrivalSchema`). Entry points whose
  // destination IS the conversation list — the profile's "See all", Back out
  // of a conversation — send `revealing`; "New conversation" tapped inside an
  // open drawer sends `dismissing`. Defaults to `closed`, so a hard reload,
  // which drops router state, just shows the landing rather than replaying an
  // entrance.
  historyArrival: gptHistoryArrivalSchema.default("closed"),
});

// Every new conversation opens the unified Mode Router surface
// (docs/gpt-mode-router.md): the composer picker is UNGATED, and Evidence is a
// mode within it (with a display-only BETA tag), so there is no longer a
// flag-based dispatch to a standalone Evidence surface — that only survives for
// legacy `evidence_gpt_page` threads on the conversation page.
export default function GPTNewChatPage({
  roomId,
}: {
  roomId: string | null;
}): React.ReactElement {
  return <GPTNewChatPageStreaming roomId={roomId} />;
}

// How a landing question reached the composer. Only the assistant engine sends
// the question of the JuisciGPT wizard (`wizard_question`).
type LandingOrigin = QuestionOrigin | AssistantOrigin;

const WIZARD_PATH = "/webapp/private/users/me/juiscigpt-wizard";

function GPTNewChatPageStreaming({
  roomId,
}: {
  roomId: string | null;
}): React.ReactElement {
  useKeyboardManaged();
  const history = useHistory();
  const { t } = useTranslation();
  const { currentUser } = useCurrentUser();
  const roomQuery = useApiQuery(
    "/webapp/private/rooms/:id",
    roomId !== null ? { param: { id: roomId } } : false
  );
  const currentRoom = roomQuery.state === "success" ? roomQuery.body : null;
  const isRoomLoading = roomQuery.state === "pending" && roomQuery.isFetching;
  const isInRoom = roomId !== null;
  const {
    initialQuestion: handedQuestion,
    initialQuestionOrigin,
    initialQuestionOwnerUid,
    initialArticle,
    askedFrom: arrivalAskedFrom,
    feedScope: arrivalFeedScope,
    historyArrival,
  } = useLocationState(locationStateSchema);
  const handedQuestionOwnership = useHandedQuestionOwnership(
    initialQuestionOwnerUid,
    handedQuestion
  );
  const ownsHandedQuestion = handedQuestionOwnership.isOwner();
  const initialQuestion = ownsHandedQuestion ? handedQuestion : null;
  const consumeLocationState = useConsumeLocationState();
  // Held past `consumeLocationState`, which empties the entry's state: a create
  // that fails leaves the reader here with the question still in the composer,
  // and the retry must mint the conversation with the same feed scope. Cleared
  // with the rest of the composer by "New conversation".
  const [feedScope, setFeedScope] = useState(arrivalFeedScope);
  // Held for the same reason: the page the question was asked from, which the
  // conversation's breadcrumb leads back to.
  const [askedFrom, setAskedFrom] = useState(arrivalAskedFrom);
  // Held for the same reason: the paper an "Ask about this study" question is
  // about, until it is referenced. A reference that fails leaves the question
  // in the composer, and its send must reference the paper first.
  const [pendingArticle, setPendingArticle] = useState(initialArticle);
  const draft = useChatComposer(
    initialQuestion === null ? "" : initialQuestion
  );
  const inputQuestion = ownsHandedQuestion ? draft.value : "";
  // The open pre-prompt category (null = the four intents). Owned here, not by
  // the menu, so focusing the composer — which swaps the vertical list for the
  // scroller above the input — keeps the drill-down where the user left it.
  const [prePromptCategoryId, setPrePromptCategoryId] =
    useState<PrePromptCategoryId | null>(null);
  // The stub a pre-prompt seeded the composer with, kept only to tag the
  // submission as suggestion-driven. `isFromPrePrompt` is derived, never
  // stored: clearing or rewriting the composer past the stub makes the question
  // the user's own again.
  const [seededPrompt, setSeededPrompt] = useState<string | null>(null);
  const isFromPrePrompt =
    seededPrompt !== null && inputQuestion.startsWith(seededPrompt);
  // ONE coordinator shared by files, references AND the question itself: all
  // three need the conversation row (the attachments FK it, and the question is
  // streamed by the conversation page, which is addressed by id). Whichever
  // comes first creates it; the others reuse it, so attaching a file and then
  // asking never mints two conversations.
  const engine = useConversationEngine();
  const conversation = useEnsureGptConversation({
    conversationId: null,
    feedScope,
    engine,
  });
  const attachments = useChatAttachments({ conversation });
  const references = useChatReferences({ conversation });
  const questionRedaction = useQuestionRedaction({
    // A created conversation keeps its engine after a flag flip; Redaction covers the assistant engine only (AD-13).
    enabled:
      (conversation.target === null ? engine : conversation.target.engine) ===
      "assistant",
    deliver: (redaction, origin: LandingOrigin) => {
      void handOver(redaction, origin);
    },
    failedTextPlace: { kind: "composer" },
  });
  // The JuisciGPT wizard (docs/juiscigpt-wizard.md): the mobile home of the
  // assistant engine, outside a room's home (REQ-22). The server decides
  // everything else: the flag, the signup date, the control group, the state.
  const wizardQuery = useApiQuery(
    WIZARD_PATH,
    isDesktopShell() || roomId !== null || engine !== "assistant" ? false : {}
  );
  const wizard = wizardQuery.state === "success" ? wizardQuery.body : null;
  const [wizardSession, setWizardSession] = useWizardSession();
  const isRoomWelcomeOpen = useIsRoomWelcomeOpen();
  const isAnnouncementShowing = useIsAnnouncementShowing();
  // The invitation waits 1 s for a reader who acts first (REQ-01).
  const isInvitationDue = useElapsed(1000);
  const revalidate = useApiRevalidate();
  // Gate A (REQ-01): one invitation per session (INV-01), never after the
  // reader touched the composer (INV-02), never over another sheet (INV-03).
  // `shown` keeps the card on screen while its record lands, as `none` then
  // `invited`.
  const invitationQuestion =
    wizard === null ||
    wizard.status === "hidden" ||
    wizard.status === "ended" ||
    wizard.gate !== "first_visit" ||
    wizardSession.composerTouched ||
    initialQuestion !== null
      ? null
      : wizard.status === "none" && wizardSession.invitation !== "answered"
        ? wizard.question
        : wizard.status === "invited" && wizardSession.invitation === "shown"
          ? wizard.question
          : null;
  const invitation =
    invitationQuestion !== null &&
    isInvitationDue &&
    !isRoomWelcomeOpen &&
    !isAnnouncementShowing
      ? invitationQuestion
      : null;
  // The trial of the tour (Figma A5): the home with the composer open (REQ-08).
  const isWizardTrial =
    wizard !== null && wizard.status === "running" && wizard.step === "trial";
  useTrackOnMount(
    "juiscigpt-wizard-invitation-shown",
    invitation === null
      ? { status: "hidden" }
      : { status: "shown", key: invitation.pairId },
    () => {
      if (invitation === null)
        throw new Error("should not happen: an impression with no invitation");
      setWizardSession((session) => ({ ...session, invitation: "shown" }));
      void recordInvitation(invitation.pairId);
    }
  );

  // The record of the invitation, so no other device invites the reader again
  // (INV-01). A failure is not caught twice: the api client reports it, the
  // reader gets no toast for a write they never asked for, and the choice of
  // the reader records the state anyway.
  async function recordInvitation(pairId: string): Promise<void> {
    try {
      await apiClient.post(`${WIZARD_PATH}/invite`, { json: { pairId } });
    } catch {
      return;
    }
    await revalidate(WIZARD_PATH);
  }

  function startTour(question: string): void {
    setWizardSession((session) => ({ ...session, invitation: "answered" }));
    handleSubmit(question, "wizard_question");
  }

  async function postponeTour(pairId: string): Promise<void> {
    setWizardSession((session) => ({ ...session, invitation: "answered" }));
    try {
      await apiClient.post(`${WIZARD_PATH}/postpone`, { json: { pairId } });
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
      return;
    }
    await revalidate(WIZARD_PATH);
  }

  // Inline `@`/`+` playlist + article mention for this page's own landing composer.
  const mention = useChatMention({
    // The engine decides where a reference is created.
    enabled: engine !== "pending",
    value: inputQuestion,
    setValue: draft.setValue,
    onAdd: references.onAdd,
    referencedKeys: references.referencedKeys,
    overlapsMatch: questionRedaction.overlapsMatch,
  });

  // A bare stub is an unfinished sentence: sending stays disabled until the user
  // has appended their own subject. An upload or a reference in flight blocks it
  // too — the question would otherwise reach the agent before the
  // file/playlist/article it is about. And so does the hand-over itself: minting
  // the conversation is a round-trip, and a second tap during it would mint a
  // second conversation.
  const sending = useSingleFlightAction();
  const isSendBlocked =
    attachments.isBusy ||
    references.isBusy ||
    sending.state.status === "running" ||
    // The engine decides where the conversation is created.
    engine === "pending";
  const isSendDisabled =
    inputQuestion.trim().length === 0 ||
    inputQuestion === seededPrompt ||
    isSendBlocked;
  // Picking a pre-prompt focuses the composer so the user types their subject
  // straight away (and the keyboard opens on the same gesture).
  const dictation = useDictationControl({
    onTranscript: draft.setValue,
    textareaRef: draft.ref,
    onSend: () => {
      iosGenerateHaptic(HapticEffect.SUCCESS);
      // A seeded (mostly-untouched) pre-prompt counts as a landing
      // suggestion; anything else the user typed is manual.
      // The composer is NOT cleared here: the question is only gone
      // once the hand-over succeeded (this page unmounts on it), and
      // if it fails the text must still be there to send again.
      submitAboutPendingArticle(
        inputQuestion,
        isFromPrePrompt ? "landing_suggestion" : "typed"
      );
    },
    canSend: () => !isSendDisabled,
  });
  const add = useChatComposerAdd();
  const [storedMode, setStoredMode] = useGptMode();
  // Desktop starts every new conversation in Auto: a Search picked once and
  // remembered would route every later question to the internal index. The
  // mode sent is written to the stored mode once the hand-over succeeded, so
  // the conversation's own "+" menu carries on with it. The phone keeps the
  // remembered preference, which its composer shows at all times.
  const [landingPick, setLandingPick] = useDesktopLandingMode();
  // The history entry of this arrival on the landing: `undefined` on the
  // entry the tab opened on, which is its own arrival too.
  // The sidebar's "New conversation" starts over on the SAME entry (it
  // remounts this page through `useNewChatGeneration`), so the generation is
  // part of the arrival too.
  const [newChatGeneration] = useNewChatGeneration();
  const arrivalKey = `${
    history.location.key === undefined ? "initial" : history.location.key
  }:${String(newChatGeneration)}`;
  const landingMode =
    landingPick.locationKey === arrivalKey ? landingPick.mode : "auto";
  const setLandingMode = (next: GptMode): void => {
    setLandingPick({ locationKey: arrivalKey, mode: next });
  };
  const mode = isDesktopShell() ? landingMode : storedMode;
  const setMode = isDesktopShell() ? setLandingMode : setStoredMode;
  // Focusing the composer on the landing collapses the hero/greeting + the
  // vertical suggestions and re-flows them into a horizontal scroller sitting
  // right above the input (keyboard-adjacent), so the keyboard never buries
  // them.
  const [isInputFocused, setIsInputFocused] = useState(false);
  // The draw for the rotating greeting, taken once per ARRIVAL on the landing
  // — i.e. per mount of this page, not of `GPTGreeting`, which unmounts and
  // comes back every time the composer takes and loses focus. Owned here so
  // that gesture cannot re-roll the phrase mid-visit.
  const greetingSeed = useMemo(() => Math.random(), []);
  // This page mounts on the LANDING (greeting + starters), not a conversation —
  // so do NOT auto-scroll to the bottom on mount (that left the empty landing
  // scrolled down). The answer never auto-follows either; the jump button
  // covers the manual case.
  const { scrollRef, isAtBottom, scrollToBottom, onScroll } =
    useChatScrollAnchor({ initialToBottom: false });
  const { gptConversationPageLink, gptNewChatPageLink } = usePageLinks(roomId);
  // A question is on its way to its conversation page while the hand-over
  // runs (the composer still holds it: it is only cleared by leaving), and
  // from the first frame of an arrival hand-off, which waits for the engine
  // before it runs.
  const isHandingOver =
    sending.state.status === "running" ||
    (initialQuestion !== null && initialQuestion.length > 0);

  // The conversation history is a panel UNDER the page, revealed by sliding the
  // whole JuisciGPT surface aside (the Rooms-menu gesture) — not an overlay.
  // There is no standalone history page: whatever else in the app means "the
  // list of conversations" (the profile's "See all", Back out of a
  // conversation) lands here with `historyArrival` set.
  // The desktop sidebar lists the conversations, so the drawer is mobile only,
  // and on desktop nothing may open it: a touchscreen laptop (or a browser
  // emulating touch) would otherwise swipe the page aside onto an empty panel.
  const hasHistoryDrawer = !isDesktopShell();
  const historyReveal = useGptHistoryReveal({
    arrival: hasHistoryDrawer ? historyArrival : "closed",
    swipeEnabled: hasHistoryDrawer,
  });

  useEffect(() => {
    gaEventGptSessionStarted();
  }, []);

  // A pre-typed hand-off waits for the engine, which decides where it is created.
  useEffect(() => {
    if (!ownsHandedQuestion) {
      consumeLocationState();
      handleNewConversation();
      handedQuestionOwnership.discard();
      return;
    }
    if (engine === "pending") return;
    if (initialQuestion !== null && initialQuestion.length > 0) {
      // Pre-typed hand-off from another surface — treated as a manual question.
      // Consumed before it is acted on: this entry stays current until the
      // post-mint redirect in `handleSubmit`, and a restore of the entry
      // (reload, browser reopen) would otherwise replay the submit.
      consumeLocationState();
      submitAboutPendingArticle(initialQuestion, initialQuestionOrigin);
    }
  }, [engine, currentUser === null ? null : currentUser.uid]);

  // Asking a question LEAVES this page: the landing composes the question, the
  // conversation page streams the answer. So the conversation row is minted here
  // (usually already there — a file or a reference creates it) and the question
  // is handed over as router state for that page to send on arrival. Nothing
  // streams here anymore, which is what puts the conversation chrome — its
  // header, its history drawer — on screen the moment the question is sent
  // instead of when the answer ends.
  // The run reads the references it finds when it starts, so a question about
  // a paper is asked once the paper is referenced. If the reference fails (a
  // toast says why), the question stays in the composer rather than going out
  // without the paper it is about, and the next send tries the paper again.
  function submitAboutPendingArticle(
    question: string,
    origin: LandingOrigin
  ): void {
    if (pendingArticle === null || engine !== "assistant") {
      handleSubmit(question, origin);
      return;
    }
    // The reader referenced it from the composer's "+" in the meantime.
    if (
      references.referencedKeys.includes(
        referenceKey("article", pendingArticle.id)
      )
    ) {
      setPendingArticle(null);
      handleSubmit(question, origin);
      return;
    }
    void references
      .onAdd("article", pendingArticle.id, pendingArticle.title)
      .then((added) => {
        if (!added) return;
        setPendingArticle(null);
        handleSubmit(question, origin);
      });
  }

  function handleSubmit(question: string, origin: LandingOrigin): void {
    // A question sent by voice or handed over never focused the composer: it
    // closes the invitation for the session too (INV-02).
    if (!wizardSession.composerTouched)
      setWizardSession((session) => ({ ...session, composerTouched: true }));
    // Redacted before the conversation exists: a failing detector leaves the
    // question in the composer and nothing on the server (OD-04).
    questionRedaction.sendRedacted(question, origin);
  }

  async function handOver(
    redaction: Redaction,
    origin: LandingOrigin
  ): Promise<void> {
    if (!handedQuestionOwnership.isOwner()) return;
    // A feed hand-off is a PubMed search by construction: sent as `general` so
    // the Mode Router cannot route it to the internal-index Search mode, which
    // never reaches PubMed (docs/gpt-mode-router.md). Decided here and not at
    // the call sites so a retry from the composer sends the same mode.
    const selectedMode: GptMode = feedScope !== null ? "general" : mode;
    await sending.run(async () => {
      try {
        const { id: conversationId, engine: created } =
          await conversation.ensure(conversation.epoch());
        if (!handedQuestionOwnership.isOwner()) return;
        // Only once the conversation exists: a failed create leaves the reader
        // here, and must not change the mode of a later, unrelated question.
        // The mode SENT, so a feed hand-off's follow-ups stay in General.
        if (isDesktopShell()) setStoredMode(selectedMode);
        // The wizard: its question starts the tour on this conversation
        // (REQ-05), and any other question while it runs is the second one,
        // which ends it (REQ-09).
        if (origin === "wizard_question") {
          if (
            wizard === null ||
            wizard.status === "hidden" ||
            wizard.status === "ended" ||
            wizard.question === null
          )
            throw new Error(
              "should not happen: a tour question without a tour"
            );
          await apiClient.post(`${WIZARD_PATH}/start`, {
            json: { pairId: wizard.question.pairId, conversationId },
          });
          await revalidate(WIZARD_PATH);
        } else if (wizard !== null && wizard.status === "running") {
          await apiClient.post(`${WIZARD_PATH}/end`, {
            json: { reason: "completed" },
          });
          await revalidate(WIZARD_PATH);
          displayToast(
            t("Tour finished · find it again in Settings › Help"),
            "success"
          );
        }
        // The conversation page asks the question as soon as it has a transcript.
        if (created === "assistant")
          seedApiQueryData(
            "/assistant/conversations/:conversationId",
            { param: { conversationId } },
            {
              status: 200,
              headers: {},
              body: {
                id: conversationId,
                title: null,
                isShared: false,
                messages: [],
              },
            }
          );
        // No analytics here: the question is ASKED by the conversation page's
        // `submit` on arrival, which fires `gaEventSearchGpt` — firing here too
        // double-counted every first question (Mixpanel 2x, 10/08→).
        // `replace`, not `push`: the landing is not a step to come back to
        // through the back stack — the conversation page's own Back returns
        // here. `?new=true` is what tells it so.
        history.replace(`${gptConversationPageLink(conversationId)}?new=true`, {
          initialQuestionOwnerUid: handedQuestionOwnership.ownerUid,
          // Carry the SUBMITTED mode so the destination sends the turn in the
          // mode chosen here, not whatever the global picker holds by the time it
          // mounts (docs/gpt-mode-router.md).
          // The assistant engine sends it as the question's request id, so a
          // restored entry replays the same run instead of asking again.
          pending: {
            question: redaction.text,
            origin,
            selectedMode,
            requestId: crypto.randomUUID(),
            notice: redaction.notice,
          },
          askedFrom,
        });
      } catch {
        // The conversation was never created, so there is nothing to hand over
        // and nowhere to go: stay on the landing with the question still in the
        // composer, and say so.
        displayToast(t("Something went wrong. Please try again."), "error");
      }
    });
  }

  // A refined pre-prompt seeds the composer with the opening of a sentence —
  // nothing is sent. The trailing space is added here (the translated stub
  // never carries invisible edge whitespace) and the composer is focused so the
  // user types their subject right after it.
  function handlePickPrePrompt(prompt: string): void {
    iosGenerateHaptic(HapticEffect.SELECTION);
    const seeded = `${prompt} `;
    setSeededPrompt(seeded);
    draft.setValue(seeded);
    const composer = draft.ref.current;
    if (composer === null)
      throw new Error("should not happen: the composer textarea is unmounted");
    composer.focus();
  }

  function handleSelectPrePromptCategory(
    categoryId: PrePromptCategoryId | null
  ): void {
    iosGenerateHaptic(HapticEffect.SELECTION);
    setPrePromptCategoryId(categoryId);
  }

  // The history panel's CTA. This page already IS the new conversation, so
  // there is nowhere to navigate: closing the panel over a composer reset to its
  // blank state is the whole action. The conversation the composer may already
  // have minted (to hold a file or a reference) is abandoned with it — leaving
  // it bound would attach the next question to a conversation carrying the
  // previous one's file, and `useChatAttachments` owns its own copy of that id,
  // so only the coordinator's `reset` can unbind both. See the hook's `reset`.
  function handleNewConversation(): void {
    historyReveal.close();
    conversation.reset();
    attachments.reset();
    references.reset();
    setPrePromptCategoryId(null);
    setSeededPrompt(null);
    draft.reset();
    questionRedaction.discardUnsent();
    setFeedScope(null);
    setAskedFrom(null);
    setPendingArticle(null);
  }

  if (roomId !== null && isRoomLoading) return <Loader expanded />;

  // The mode is the "+" menu's on desktop, where the composer's button row keeps
  // only "+", dictation and send (Figma). The assistant engine has no modes.
  const isDesktop = isDesktopShell();
  const modeChoice =
    engine === "assistant" ? null : { selected: mode, onSelectChange: setMode };
  const composer = (
    <>
      <ChatMentionDropdown
        results={mention.results}
        onSelect={mention.onSelect}
      />
      <ChatAttachmentChips attachments={attachments} canRemove />
      <ChatReferenceChips references={references} canRemove />
      <ChatInput
        ref={draft.ref}
        className="w-full"
        variant={isDesktop ? "docked" : "default"}
        placeholder={
          isDesktop
            ? t("Describe a case, a decision, or a clinical doubt…")
            : isWizardTrial
              ? t("A question you know the answer to…")
              : t("Ask a medical question...")
        }
        value={inputQuestion}
        readOnly={dictation.composerReadOnly}
        // The trial of the wizard opens the composer (REQ-08).
        autoFocus={isWizardTrial}
        onFocus={() => {
          if (dictation.isActive) dictation.stop();
          setIsInputFocused(true);
          if (!wizardSession.composerTouched)
            setWizardSession((session) => ({
              ...session,
              composerTouched: true,
            }));
        }}
        onBlur={() => {
          setIsInputFocused(false);
        }}
        onChange={(e) => {
          draft.setValue(e.target.value);
        }}
        mode={isDesktop ? null : modeChoice}
        leading={
          // The "+" (File / Playlist / Article) leads the row and owns
          // file picking, so `attach` is null here.
          <ChatComposerAddTrigger
            add={add}
            attachments={attachments}
            references={references}
            disabled={engine === "pending"}
            mode={isDesktop && modeChoice !== null ? modeChoice.selected : null}
          />
        }
        leadingPanel={
          add.state.view === "closed"
            ? null
            : {
                id: add.panelId,
                content: (
                  <ChatComposerAddPanel
                    add={add}
                    references={references}
                    mode={isDesktop ? modeChoice : null}
                  />
                ),
                onDismiss: add.close,
                placement: isDesktop ? "below" : "above",
              }
        }
        banner={
          questionRedaction.unsent.length > 0 ? (
            <PatientDataBanner content={{ kind: "checkFailed" }} />
          ) : questionRedaction.showInputRule ? (
            <PatientDataBanner
              content={{
                kind: "inputRule",
                onDismiss: questionRedaction.dismissInputRule,
              }}
            />
          ) : null
        }
        attach={null}
        dictation={dictation.composer}
        stream={null}
        onSend={dictation.send}
        // While dictating, emptiness is not a block: the send waits for the
        // transcript (see `useDictationControl`).
        sendDisabled={dictation.isActive ? isSendBlocked : isSendDisabled}
      />
    </>
  );

  if (isDesktop && isHandingOver)
    return (
      <DesktopHandOffShell
        question={questionRedaction.preview(inputQuestion)}
        askedFrom={askedFrom}
        newChatLink={gptNewChatPageLink()}
        composer={
          <ChatInput
            className="w-full"
            variant="docked"
            placeholder={t("Describe a case, a decision, or a clinical doubt…")}
            value=""
            readOnly
            mode={null}
            leading={
              <ChatComposerAddTrigger
                add={add}
                attachments={attachments}
                references={references}
                disabled
                mode={null}
              />
            }
            leadingPanel={null}
            banner={null}
            attach={null}
            dictation={null}
            // The conversation page draws a turn in flight with its Stop.
            stream={{
              kind: "streaming",
              onStop: () => {
                throw new Error(
                  "should not happen: the hand-off shell is inert"
                );
              },
            }}
            onSend={() => {
              throw new Error("should not happen: the hand-off shell is inert");
            }}
            sendDisabled
          />
        }
      />
    );

  // Desktop (Figma "D3.explore.pour-vous"): the greeting, the composer and the
  // starters are one block centred in the content area, beside the sidebar
  // that lists the conversations. No drawer, no bottom bar: the composer sits
  // in the flow instead of being fixed to the bottom. On a native tablet the
  // shell keeps the WebView above the keyboard, so the flow still fits.
  if (isDesktop)
    return (
      <div className="flex h-full flex-col overflow-y-auto bg-ds-canvas">
        {/* Figma "D1.accueil": the block sits above the centre (32 over,
            188 under), its composer column 836px inside 72px gutters. The
            greeting may run wider, so a longer phrase stays on one line.
            Only the greeting and the composer are centred: the starters and
            the notice hang under the composer, out of the flow, so opening an
            intent grows its list downwards and leaves the composer where it
            was. The bottom padding takes their 100px back (188 + 100), which
            keeps the block's resting place. */}
        <div className="flex w-full flex-1 flex-col items-center justify-center gap-2.5 pt-8 pb-72">
          <div className="w-full max-w-gpt-greeting px-8 py-2">
            <GPTGreeting seed={greetingSeed} size="desktop" />
          </div>
          <div className="relative w-full max-w-gpt-landing px-18">
            <div className="relative w-full">{composer}</div>
            <div className="absolute inset-x-0 top-full flex flex-col gap-3 px-18 pt-3 pb-8">
              <GPTPrePrompts
                layout="inline"
                className="animate-fade-in"
                selectedCategoryId={prePromptCategoryId}
                onSelectCategory={handleSelectPrePromptCategory}
                onPickPrompt={handlePickPrePrompt}
              />
              <div className="py-2">
                <GptDesktopNotice reveal="static" />
              </div>
            </div>
          </div>
        </div>
        {/* Figma "D1.accueil" (5736:129424): the desktop signup no longer asks
            how the practitioner heard of Juisci, so the home asks it while
            the account has no answer. */}
        {currentUser !== null && currentUser.acquisitionChannel === null && (
          <DesktopAcquisitionCard />
        )}
      </div>
    );

  return (
    <>
      {hasHistoryDrawer ? (
        <GPTHistoryPanel
          isOpen={historyReveal.isOpen}
          roomId={roomId}
          onNewConversation={handleNewConversation}
          // The landing shows no conversation, so a deleted row leaves nothing to
          // navigate away from — the list revalidates itself.
          onConversationDeleted={() => {}}
        />
      ) : null}
      <ScrollableLayout
        containerClassName={historyReveal.containerClassName}
        containerStyle={historyReveal.containerStyle}
        background="var(--color-ds-cream)"
        scrollRef={scrollRef}
        onScroll={onScroll}
        style={historyReveal.scrollLockStyle}
        // Make the scroll area a flex column so the content column can fill the
        // viewport and a flexible spacer (below) absorbs the slack — no
        // scroll/rubber-band when the suggestions already fit on screen.
        className="flex flex-col"
        hideScrollbar
        header={
          <div
            className={cn(
              GPT_HEADER_ROW,
              hasHistoryDrawer ? "justify-between" : "justify-end"
            )}
          >
            {hasHistoryDrawer ? (
              <GPTHistoryButton onClick={historyReveal.open} className={null} />
            ) : null}

            {/* The avatar every other page's `Header` puts on the right,
                reproduced here because this landing draws its own header
                rather than using that component. */}
            {currentUser === null ? (
              <Link to="/login">
                <IconUserCircle size={26} className="text-primary-black" />
              </Link>
            ) : (
              <Link to="/profile">
                <ProfilePicture user={currentUser} height={32} badge={null} />
              </Link>
            )}
          </div>
        }
        headerClassName="bg-ds-cream"
        footer={<Navbar />}
      >
        {hasHistoryDrawer ? (
          <GPTHistoryScrim
            isOpen={historyReveal.isOpen}
            onClose={historyReveal.close}
          />
        ) : null}
        <div className="page-wrapper flex flex-1 flex-col">
          <GPTConversationWrapper className="bg-ds-cream pt-4 pb-0">
            {isInRoom && currentRoom && FEATURE_FLAG_JUISCIGPT_FOR_ROOMS ? (
              <div className="px-5 flex flex-col items-center gap-[10px]">
                <RoomLogo logoUrl={getRoomLogoUrl(currentRoom)} />
                <p className="m-0 text-center">
                  <Trans
                    i18nKey="Ask JuisciGPT questions based solely on the scientific literature of the <b>{{roomName}} room</b> of {{organisationName}}"
                    values={{ roomName: "TEST", organisationName: "ORG" }}
                    components={{ b: <b /> }}
                  />
                </p>
              </div>
            ) : (
              /* Landing view: greeting above, starters hugging the composer */
              <div className="flex flex-1 flex-col px-5 md:pb-32">
                {/* Greeting + vertical starters collapse once the composer is
                  focused — the horizontal scroller above the input takes over. */}
                {!isInputFocused && (
                  <>
                    {/* The greeting and the starters split the free space in
                      half, `min-h-0` making each half exactly that — a share of
                      the container, never a function of its own content. That
                      is what pins the title: drilling into a category grows the
                      list from four rows to six, and if the halves were sized
                      by content the title would slide up to make room. It is
                      centred in the top half and stays there. */}
                    <div className="flex min-h-0 flex-1 items-center justify-center py-6">
                      <GPTGreeting seed={greetingSeed} size="mobile" />
                    </div>

                    {/* Conversation starters: intent → refined pre-prompt,
                      which pre-fills the composer below. Bottom-aligned in its
                      half so the list hugs the composer, and `-safe` so a level
                      taller than that half falls back to top-alignment and
                      scrolls instead of overflowing out of reach (the classic
                      `justify-end` overflow trap). */}
                    <GPTPrePrompts
                      layout="column"
                      className="min-h-0 flex-1 justify-end-safe overflow-y-auto animate-fade-in [-ms-overflow-style:none] scrollbar-none [&::-webkit-scrollbar]:hidden"
                      selectedCategoryId={prePromptCategoryId}
                      onSelectCategory={handleSelectPrePromptCategory}
                      onPickPrompt={handlePickPrePrompt}
                    />
                  </>
                )}

                {/* Bottom reservation for the fixed composer, which the
                  landing column scrolls behind. The composer block measures
                  116px (10 top + 90 ChatInput + 16 bottom), so the ChatInput
                  card's own top edge is at 106 — the distance to measure the
                  gap against, since the 10px above it are just the bar's own
                  cream padding. 126 leaves 20px there: Figma draws 8, which
                  reads as glued once the chips carry their real hover box, so
                  this is deliberately one notch looser than the mock.
                  Unlike the conversation transcript (which reserves the band's
                  *measured* height) this is a FIXED reservation on purpose: it
                  must NOT grow with the starter row the composer stacks above
                  itself, because the greeting block above absorbs the slack,
                  and that is what pins the starters to the composer.
                  Removed on desktop, where md:pb-32 handles the clearance. */}
                <div
                  aria-hidden
                  className="w-full shrink-0 h-[calc(126px+var(--safe-area-bottom))] md:hidden"
                />
              </div>
            )}
          </GPTConversationWrapper>
        </div>

        {/* The keyboard is a window-level signal, not a per-input one: the
                history panel's search field opens it too, and the composer —
                pushed aside with the page and already non-interactive — would
                ride up alongside the panel's keyboard for no reason. */}
        <KeyboardAvoidingView
          behavior="position"
          enabled={!historyReveal.isOpen}
          transitionDuration={0.22}
          style={{
            position: "fixed",
            // Sits above the bottom bar, which absorbs safe-area/2.
            bottom:
              "calc(var(--navbar-height, 72px) + var(--safe-area-bottom) / 2)",
            width: "100%",
          }}
        >
          {/* Anchored above the input block so it tracks its real top edge on
            every platform (safe-area, keyboard) instead of a fixed offset. */}
          <ScrollDownButton
            visible={!isAtBottom}
            onClick={() => {
              scrollToBottom();
            }}
            className="absolute -top-12 left-1/2 -translate-x-1/2 z-10"
          />
          {/* Focused landing: the starters re-flow into a horizontal
            scroller hugging the top edge of the composer, at whatever level
            the user had drilled to. Native touch scrolling drives the drag;
            the browser cancels the click once a swipe is recognised, so a drag
            scrolls the list instead of firing a suggestion (submit on click
            only — never on pointerdown). */}
          {isInputFocused ? (
            <GPTPrePrompts
              layout="row"
              // No container animation: each pill slides up on its own delay,
              // and a wrapper transform on top of that reads as a double move.
              className={cn("px-5 pb-3", GPT_DESKTOP_COLUMN)}
              selectedCategoryId={prePromptCategoryId}
              onSelectCategory={handleSelectPrePromptCategory}
              onPickPrompt={handlePickPrePrompt}
            />
          ) : null}
          {isWizardTrial ? (
            <p className="bg-ds-cream px-5 pt-3 text-base font-semibold text-ds-mint-900">
              {t("Which question do you know by heart?")}
            </p>
          ) : null}
          <div
            className={cn(
              "relative w-full px-5 pt-[10px] pb-4 bg-ds-cream",
              GPT_DESKTOP_COLUMN
            )}
          >
            {composer}
          </div>
        </KeyboardAvoidingView>
      </ScrollableLayout>
      {invitation !== null ? (
        <WizardInvitation
          question={invitation}
          onStart={() => {
            startTour(invitation.text);
          }}
          onLater={() => {
            void postponeTour(invitation.pairId);
          }}
        />
      ) : null}
    </>
  );
}

/**
 * What the desktop landing shows while it hands a question over (mint the
 * conversation, then `replace` to its page): that page's chrome with the
 * question already asked, so the reader goes from the page they asked on
 * straight to the conversation (Figma "D2.conversation.from-quick-task"). The
 * landing itself would otherwise flash in between. It lasts one round-trip, so
 * nothing in it responds: `inert` takes it out of the pointer, the focus order
 * and the accessibility tree (React 18 has no prop for it).
 */
function DesktopHandOffShell({
  question,
  askedFrom,
  newChatLink,
  composer,
}: {
  // Null when the detector failed, which the hand-off reports.
  question: string | null;
  askedFrom: AskedFrom;
  newChatLink: string;
  composer: React.ReactNode;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div
      ref={(shell) => {
        if (shell !== null) shell.inert = true;
      }}
      className="flex h-full flex-col bg-ds-canvas"
    >
      <DesktopTopNav
        context={
          <GptConversationBreadcrumb
            askedFrom={askedFrom}
            newChatLink={newChatLink}
            onBackToGpt={() => {
              throw new Error("should not happen: the hand-off shell is inert");
            }}
            current={t("New conversation")}
          />
        }
        trailing={{ kind: "actions", content: null }}
        surface="default"
        className="static bg-ds-canvas"
      />
      <GPTConversationWrapper
        className={cn("flex-1 bg-ds-canvas pt-4", GPT_DESKTOP_COLUMN)}
      >
        {question !== null && (
          <div className="mr-5 ml-auto w-fit">
            <ChatMessageCard variant={isDesktopShell() ? "desktop" : "default"}>
              <RedactedQuestion text={question} />
            </ChatMessageCard>
          </div>
        )}
        <GPTToolStep kind="thinking" />
      </GPTConversationWrapper>
      <div
        className={cn(
          "flex w-full flex-col gap-2 bg-ds-canvas px-5 pt-3 pb-4",
          GPT_DESKTOP_COMPOSER
        )}
      >
        {composer}
        <GptDesktopNotice reveal="static" />
      </div>
    </div>
  );
}
